import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Chip, Grid, TextField, Checkbox, Paper, List, InputLabel, FormControl, Select, MenuItem, RadioGroup, Radio } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { Autocomplete } from '@material-ui/lab';
import { toast } from 'react-toastify';
import moment from 'moment';
import MaterialDatePicker from '../../global/form/MaterialDatePicker';
import { IsEmpty } from '../../../common/checks';
import { Trim } from '../../../common/util';
import Constants, { PageType } from '../../../common/constants';

const Mechanics = ({
  postStatus, pageType, mallHighlight, onMallHighlightChanged, ignoreHttpsCheck, onIgnoreHttpsCheckChanged,
  promoCategories, categories, title, description, promoCodes, promoType, dateFrom, dateTo, showPicMarker,
  onTitleChanged, onDescriptionChanged, onDateFromChanged, onDateToChanged, onPromoCategoriesChanged, onPromoCodesChanged,
  onPromoTypeChanged, onShowPicMarkerChanged }) => {
  const [promoCode, setInputPromoCode] = useState('');
  const [promoCodesDataState, setPromoCodesDataState] = useState([]);
  const [currentCategories, setCurrentCategories] = useState([]);
  const [categorySelection, setCategorySelection] = useState([]);
  const [tempFrom, setTempFrom] = useState('');
  const [tempTo, setTempTo] = useState('');
  const [toggleLockDates, setToggleLockDates] = useState(true);
  const [updatedPromoType, setUpdatedPromoType] = useState('');

  useEffect(() => {
    setUpdatedPromoType(promoType);
  }, [promoType]);

  useEffect(() => {
    setPromoCodesDataState(promoCodes);
  }, [promoCodes, promoCodesDataState]);

  useEffect(() => {
    if (!dateFrom && !dateTo) {
      setToggleLockDates(false);
    } else {
      setTempFrom(dateFrom);
      setTempTo(dateTo);
    }
  }, []);

  useEffect(() => {
    if (new Date(dateFrom) > new Date(dateTo)) {
      onDateFromChanged(tempFrom);
      onDateToChanged(tempTo);
      toast.error(`Promo end date is earlier than current date`);
    }
  }, [dateFrom, dateTo]);

  const updateCategorySelection = () => {
    const set = new Set();
    let list = [];
    _.forEach(promoCategories, (category) => set.add(category.id));
    _.forEach(categories, (category) => {
      if (!set.has(category.id)) { list.push(category); }
    });

    list = _.mapValues(_.groupBy(list, 'group'), (v) => _.sortBy(v, 'name'));
    const temp = [];
    const sortList = Object.keys(list).sort();

    for (let x = 0; x < sortList.length; x += 1) {
      temp.push(list[sortList[x]]);
    }

    const newList = [].concat(...temp);

    setCategorySelection(newList);
  };

  useEffect(() => {
    setCurrentCategories(promoCategories);
    updateCategorySelection();
  }, [promoCategories]);

  useEffect(() => {
    updateCategorySelection();
  }, [currentCategories, categories]);

  const resetDate = () => {
    // set default current dates
    const current = new Date();
    current.setHours(0, 0, 0, 0); //to prevent errors when current promoFromDate === promoEndDate
    dateFrom = tempFrom || current.toISOString();
    onDateFromChanged(dateFrom);
    dateTo = tempTo || new Date(current.setDate(current.getDate() + 7)).toISOString();
    onDateToChanged(dateTo);
  };

  const onChange = (e) => {
    switch (e.target.name) {
      case 'title':
        title = e.target.value;
        onTitleChanged(title);
        break;
      case 'description':
        description = e.target.value;
        onDescriptionChanged(description);
        break;
      case 'promoCodes':
        setInputPromoCode(e.target.value);
        break;
      case 'promoType':
        setUpdatedPromoType(e.target.value);
        onPromoTypeChanged(e.target.value);
        break;
      default:
    }
  };

  const onChangePromoCodes = (e) => {
    const newPromoCode = Trim(e.target.value.toString().toUpperCase());
    if (!promoCodesDataState.includes(newPromoCode) && !IsEmpty(newPromoCode)) {
      promoCodesDataState.push(newPromoCode);

      onPromoCodesChanged(promoCodesDataState);
      setPromoCodesDataState(promoCodesDataState);

      // clear input
      setInputPromoCode('');
    }
  };

  const handleDeletePromoCode = (value) => {
    const promoCodeFiltered = promoCodesDataState.filter((item) => item !== value);
    onPromoCodesChanged(promoCodeFiltered);
    setPromoCodesDataState(promoCodeFiltered);
  };

  const handlePeriodUnspecified = (toggle) => {
    if (toggle) {
      setToggleLockDates(false);

      // nullify dates
      onDateFromChanged(null);
      onDateToChanged(null);
    } else {
      setToggleLockDates(true);
      resetDate();
    }
  };

  const handleMallHighlight = (e) => {
    mallHighlight = e.target.checked;
    onMallHighlightChanged(mallHighlight);
  };

  const handleIgnoreHttpsCheck = (e) => {
    ignoreHttpsCheck = e.target.checked;
    onIgnoreHttpsCheckChanged(ignoreHttpsCheck);
  };

  const formatDate = (date) => {
    const newDate = new Date(date);
    return moment(newDate).format('YYYY-MM-DD');
  };

  const onChangeFrom = (e = new Date()) => {
    dateFrom = e.toString('yyyy-MM-dd');
    onDateFromChanged(formatDate(dateFrom));
  };

  const onChangeTo = (e) => {
    dateTo = e.toString('yyyy-MM-dd');
    onDateToChanged(formatDate(dateTo));
  };

  const isStatusLiveOrPending = () => [Constants.Status.Live, Constants.Status.Pending].includes(postStatus);

  return (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <h5>Promotion Title</h5>
        <TextField
          name="title"
          placeholder="Enter Promotion Title"
          multiline
          className="d-flex"
          value={title}
          onChange={(e) => onChange(e)}
        />
      </Grid>

      {pageType.toString().toUpperCase() === 'MALL' && (
        <Grid item container>
          <Grid item>
            <h5>Mall Highlights</h5>
          </Grid>

          <Grid item container direction="row">
            <Grid item>
              <FormControlLabel control={<Checkbox onChange={(e) => handleMallHighlight(e)} checked={mallHighlight} />} label="Promoting on-behalf of store" />
              {
                mallHighlight && (
                  <div>
                    <p style={{ color: '#f50057', marginLeft: 25 }}>
                      * Please select the
                      {' '}
                      <b>Promotion Category</b>
                    </p>
                    <p style={{ color: '#f50057', marginLeft: 25 }}>
                      * Please enter store name as a partner under
                      {' '}
                      <b>Partnerships, Tags and Post Date</b>
                    </p>
                  </div>
                )
              }
            </Grid>
          </Grid>
        </Grid>
      )}

      <Grid item container>
        <Grid item>
          <h5>Promotion Period</h5>
        </Grid>

        {
          (!isStatusLiveOrPending() || ((IsEmpty(dateFrom) && !IsEmpty(dateTo)) || (!IsEmpty(dateFrom) && IsEmpty(dateTo))))
            ? (
              <>
                <Grid item container direction="row">
                  <Grid item>
                    {(!isStatusLiveOrPending()) && (
                      <FormControlLabel
                        control={
                          <Checkbox onChange={() => handlePeriodUnspecified(toggleLockDates)} checked={!toggleLockDates} />
                        }
                        label="Period unspecified"
                      />
                    )}
                    {(
                      toggleLockDates && (
                        (IsEmpty(dateFrom) && !IsEmpty(dateTo)) || (!IsEmpty(dateFrom) && IsEmpty(dateTo))
                      )) && <p style={{ color: 'red' }}>We detected one of the dates is missing please re-populate the dates. Make sure to save the changes.</p>}
                  </Grid>
                </Grid>

                { toggleLockDates && (
                  <Grid item container direction="row" spacing={2}>
                    <Grid item>
                      <MaterialDatePicker
                        minDate={false}
                        label="Promotion starts from"
                        value={
                          !toggleLockDates ? null : IsEmpty(dateFrom) ? null : new Date(dateFrom)
                        }
                        onChange={(e) => onChangeFrom(e)}
                      />
                    </Grid>
                    <Grid item>
                      <MaterialDatePicker
                        minDate={false}
                        label="Promotion ends on"
                        value={
                          !toggleLockDates ? null : IsEmpty(dateTo) ? null : new Date(dateTo)
                        }
                        onChange={(e) => onChangeTo(e)}
                      />
                    </Grid>
                  </Grid>
                ) }
              </>
            )
            : (
              <>
                <Grid item container direction="row">
                  <Grid item>
                    <div className="alert alert-warning" role="alert">
                      Promo dates cannot be change once the post is Live. Set it back to
                      {' '}
                      <strong>Draft</strong>
                      {' '}
                      status to update the dates.
                      <br />
                      <hr />
                      { dateFrom && dateTo ? (
                        <div>
                          <h5><strong>{`Promo starts: ${dateFrom}`}</strong></h5>
                          <h5><strong>{`Promo ends: ${dateTo}`}</strong></h5>
                        </div>
                      ) : <h5>Promo period unspecified</h5>}
                    </div>
                  </Grid>
                </Grid>
              </>
            )
        }
      </Grid>

      <Grid item>
        <h5>Promotion Category</h5>
        <Autocomplete
          limitTags={5}
          multiple
          id="tags-outlined"
          options={categorySelection}
          disableCloseOnSelect
          disablePortal
          noOptionsText="Category is not available."
          value={currentCategories}
          onChange={(e, values) => {
            setCurrentCategories(values);
            onPromoCategoriesChanged(values);
          }}
          renderTags={(value, getTagProps) => value.map((option, index) => (
            <Chip variant="outlined" label={option.name} {...getTagProps({ index })} />
          ))}
          groupBy={(option) => option.group}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
            />
          )}
        />
      </Grid>
      <Grid item>
        <h5>Promotion Description</h5>
        <FormControlLabel control={<Checkbox onChange={(e) => handleIgnoreHttpsCheck(e)} checked={ignoreHttpsCheck} />} label="Ignore HTTPS check" />
        <TextField name="description" placeholder="Enter Promotion Description" multiline className="d-flex" value={description} onChange={(e) => onChange(e)} />
      </Grid>

      <Grid item>
        <Paper style={{ maxHeight: 300, overflow: 'hidden', padding: 10, marginTop: 10 }}>
          <h5>
            Promo Codes (
            {promoCodesDataState.length}
            )
          </h5>
          <TextField
            name="promoCodes"
            placeholder="Enter a Promo Code"
            className="d-flex"
            value={promoCode}
            autoComplete="off"
            onChange={(e) => onChange(e)}
            onBlur={(e) => onChangePromoCodes(e)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onChangePromoCodes(e);
              }
            }}
          />
          <p className="mt-2" style={{ fontSize: 13, textDecoration: 'none', color: '#3f51b5' }}>Click to add</p>
          <hr />
          { promoCodesDataState.length > 0
            ? (
              <List style={{ overflowY: 'auto', height: 200 }}>
                <div className="d-block">
                  {
                    promoCodesDataState.map((value, index) => (
                      <div className="d-inline-block" style={{ position: 'relative', background: '#a0c6ff', borderRadius: 4, color: '#000', letterSpacing: 1, fontSize: 15, fontWeight: 'bold', padding: '10px 10px 10px 15px', margin: 10 }} key={index}>
                        <span className="mr-2">{value}</span>
                        {' '}
                        <ClearIcon style={{ position: 'absolute', background: '#FFF', border: '2px solid #a0c6ff', color: '#a0c6ff', top: -5, right: -5, borderRadius: '100%', padding: 3, cursor: 'pointer' }} onClick={() => handleDeletePromoCode(value)} />
                      </div>
                    ))
                  }
                </div>
              </List>
            )
            : 'No promo code(s) found.' }
        </Paper>
      </Grid>

      <Grid item>
        <h5>Promo Type</h5>
        <FormControl variant="outlined" className="mt-3">
          <InputLabel id="promo-type-select-label">Select</InputLabel>
          <Select
            labelId="promo-type-select-label"
            id="promo-type-select"
            name="promoType"
            value={updatedPromoType}
            onChange={(e) => onChange(e)}
            style={{ width: 300 }}
            label="Select"
          >
            <MenuItem value={Constants.PromoType.Coupon}>{Constants.PromoType.Coupon}</MenuItem>
            <MenuItem value={Constants.PromoType.CreditCard}>{Constants.PromoType.CreditCard}</MenuItem>
            <MenuItem value={Constants.PromoType.Deals}>{Constants.PromoType.Deals}</MenuItem>
            <MenuItem value={Constants.PromoType.Events}>{Constants.PromoType.Events}</MenuItem>
            <MenuItem value={Constants.PromoType.LuckyDraw}>{Constants.PromoType.LuckyDraw}</MenuItem>
            <MenuItem value={Constants.PromoType.NewLaunch}>{Constants.PromoType.NewLaunch}</MenuItem>
            <MenuItem value={Constants.PromoType.PromoCode}>{Constants.PromoType.PromoCode}</MenuItem>
            <MenuItem value={Constants.PromoType.Vouchers}>{Constants.PromoType.Vouchers}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {
        pageType === PageType.View && (
          <Grid item>
            <h5>Show as Picture Marker</h5>
            <RadioGroup
              row
              aria-label="type"
              name="tagSearchInMapEnabled"
              value={showPicMarker}
            >
              <FormControlLabel
                onChange={() => onShowPicMarkerChanged(false)}
                value={false}
                control={<Radio color="primary" checked={!showPicMarker} />}
                label="No"
              />
              <FormControlLabel
                onChange={() => onShowPicMarkerChanged(true)}
                value
                control={<Radio color="primary" checked={showPicMarker} />}
                label="Yes"
              />
            </RadioGroup>
          </Grid>
        )
      }
    </Grid>
  );
};

Mechanics.propTypes = {
  postStatus:       PropTypes.string.isRequired,
  pageType:         PropTypes.string.isRequired,
  mallHighlight:    PropTypes.bool,
  ignoreHttpsCheck: PropTypes.bool,
  categories:       PropTypes.arrayOf(PropTypes.shape({ })).isRequired,
  promoCategories:  PropTypes.arrayOf(PropTypes.shape({ })).isRequired,
  title:            PropTypes.string,
  description:      PropTypes.string,
  promoCodes:       PropTypes.arrayOf(PropTypes.string),
  promoType:        PropTypes.string.isRequired,
  dateFrom:         PropTypes.string,
  dateTo:           PropTypes.string,
  showPicMarker:    PropTypes.bool,

  onMallHighlightChanged:    PropTypes.func.isRequired,
  onIgnoreHttpsCheckChanged: PropTypes.func.isRequired,
  onTitleChanged:            PropTypes.func.isRequired,
  onDescriptionChanged:      PropTypes.func.isRequired,
  onDateFromChanged:         PropTypes.func.isRequired,
  onDateToChanged:           PropTypes.func.isRequired,
  onPromoCategoriesChanged:  PropTypes.func.isRequired,
  onPromoCodesChanged:       PropTypes.func.isRequired,
  onPromoTypeChanged:        PropTypes.func.isRequired,
  onShowPicMarkerChanged:    PropTypes.func.isRequired,
};

Mechanics.defaultProps = {
  mallHighlight:    false,
  ignoreHttpsCheck: false,
  title:            '',
  description:      '',
  promoCodes:       [],
  dateFrom:         null,
  dateTo:           null,
  showPicMarker:    false,
};

export default connect(null, null)(Mechanics);
